import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { OSCE } from '@ils/osces/db/osce.js';
import { Names } from '@ils/static/names.js';
import { DateTime } from 'luxon';
import { ExamSlotRating } from './slotRating.js';

@Entity()
export class ExamSlotRatingDoc extends MongoEntity {
  static override _class = Names.ExamSlotRatingDoc._class;
  static override _collectionName = Names.ExamSlotRatingDoc._collectionName;

  static override get allFields() {
    return selectionSet(() => ExamSlotRatingDoc, 3);
  }

  @Field(type => ID)
  slotID!: string;

  @Field(type => ID)
  inspector?: Maybe<string>;

  @Field(type => Boolean)
  committed!: boolean;

  @Field(type => Boolean)
  archived: Maybe<boolean>;

  @Field(type => [ExamSlotRating])
  ratings!: ExamSlotRating[];

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  createdAt!: DateTime;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  updatedAt!: DateTime;

  @Field(type => String)
  notes: Maybe<string>;

  static override beforeCreate(entity: MongoEntity): Promise<void> {
    throw new Error('use the rating doc factory!');
  }

  static override async beforeUpdate(
    oldEntity: PartialEntityData<ExamSlotRatingDoc>,
    update: MongoEntity,
  ): Promise<void> {
    if (oldEntity.committed) throw new Error('committed documents cant be updated!');
  }

  static fromOSCE(osce: OSCE, inspector: string) {
    return new ExamSlotRatingDoc({
      inspector,
      committed: false,
      ratings: (osce.instructionCategories ?? [])
        .flatMap(cat => cat.instructions)
        .map(instruction => new ExamSlotRating({ instructionId: instruction._id, score: -1 })),
    });
  }

  allRated() {
    return this.ratings.every(rating => rating.score !== -1);
  }
}
